import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { finalize } from 'rxjs';
import { OrderImageService } from '../../services/order-image.service';

@Component({
  selector: 'app-order-image-upload',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="order-image-upload text-start">
      <label class="form-label d-block">
        Adjuntar imagen a la orden <span class="text-muted">(opcional)</span>
        <input
          #imageInput
          class="form-control mt-2"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          [disabled]="isUploading"
          (change)="onFileSelected($event)"
        />
      </label>
      <small class="form-text text-muted d-block">JPG, PNG o WebP. Máximo 5 MB.</small>
      <small *ngIf="selectedFile" class="d-block mt-1" aria-live="polite">
        {{ selectedFile.name }} ({{ selectedFile.size / 1024 | number:'1.0-0' }} KB)
      </small>
      <button
        type="button"
        class="btn btn-outline-info btn-sm mt-2"
        [disabled]="!selectedFile || isUploading"
        (click)="uploadImage(imageInput)"
      >
        {{ isUploading ? 'Subiendo imagen...' : 'Subir imagen' }}
      </button>
      <small *ngIf="errorMessage" class="d-block mt-2 text-danger" role="alert">
        {{ errorMessage }}
      </small>
      <small *ngIf="successMessage" class="d-block mt-2 text-success" role="status">
        {{ successMessage }}
      </small>
    </div>
  `
})
export class OrderImageUploadComponent {
  @Input({ required: true }) orderId = '';

  selectedFile: File | null = null;
  errorMessage = '';
  successMessage = '';
  isUploading = false;

  private readonly maxFileSize = 5 * 1024 * 1024;
  private readonly allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];

  constructor(private readonly orderImageService: OrderImageService) {}

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    this.selectedFile = null;
    this.errorMessage = '';
    this.successMessage = '';

    if (!file) {
      return;
    }

    if (!this.allowedTypes.includes(file.type)) {
      this.errorMessage = 'Selecciona una imagen JPG, PNG o WebP.';
      input.value = '';
      return;
    }

    if (file.size > this.maxFileSize) {
      this.errorMessage = 'La imagen no debe superar 5 MB.';
      input.value = '';
      return;
    }

    this.selectedFile = file;
  }

  uploadImage(input: HTMLInputElement): void {
    if (!this.selectedFile || this.isUploading) {
      return;
    }

    this.isUploading = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.orderImageService.upload(this.orderId, this.selectedFile)
      .pipe(finalize(() => this.isUploading = false))
      .subscribe({
        next: response => {
          this.successMessage = response.message || 'Imagen adjuntada a la orden.';
          this.selectedFile = null;
          input.value = '';
        },
        error: error => {
          this.errorMessage = error?.error?.message || 'No se pudo subir la imagen. Inténtalo de nuevo.';
        }
      });
  }
}